export type CriterionKey =
  | "instruction"
  | "facilities"
  | "community"
  | "value"
  | "beginner";

// Qualitative editorial assessment per criterion. We deliberately avoid
// numeric scores: a word-tier reads as honest editorial judgment rather than
// implying a measured, decimal-precise rating we cannot actually substantiate.
export type CriterionRating = "exceptional" | "excellent" | "strong" | "solid";

export type StudioRatings = Record<CriterionKey, CriterionRating>;

export interface PotteryStudio {
  id: string;
  rank: number;
  name: string;
  neighborhood: string;
  borough: string;
  website: string;
  description: string;
  highlights: string[];
  classTypes: string[];
  ratings: StudioRatings;
  editorialNote: string;
  // Only the top 3 studios carry a "best for" badge.
  accolade?: string;
}

export const RANKING_CRITERIA = [
  {
    key: "instruction",
    label: "Instruction Quality",
    emphasis: "Primary factor",
    description:
      "Clarity of teaching, instructor experience, and the quality of hands-on feedback at the wheel.",
  },
  {
    key: "facilities",
    label: "Studio & Equipment",
    emphasis: "Major factor",
    description:
      "Wheel availability, kiln access, workspace quality, and overall studio upkeep.",
  },
  {
    key: "community",
    label: "Community & Atmosphere",
    emphasis: "Major factor",
    description:
      "Welcoming environment, peer support, and opportunities to connect with other makers.",
  },
  {
    key: "value",
    label: "Value & Flexibility",
    emphasis: "Major factor",
    description:
      "Pricing transparency, membership options, open-studio access, and class variety.",
  },
  {
    key: "beginner",
    label: "Beginner Experience",
    emphasis: "Supporting factor",
    description:
      "How well first-timers are supported—intro-level classes, no-experience-needed options, and a clear path from your first session onward.",
  },
] as const;

export const RATING_TIERS: Array<{
  key: CriterionRating;
  label: string;
  note: string;
}> = [
  { key: "exceptional", label: "Exceptional", note: "Among the very best in the city" },
  { key: "excellent", label: "Excellent", note: "A clear strength of the studio" },
  { key: "strong", label: "Strong", note: "Reliably good" },
  { key: "solid", label: "Solid", note: "Dependable, if not a standout" },
];

export const EDITORIAL_DISCLAIMER =
  "Rankings on this site are editorial comparisons prepared for demonstration purposes. Our assessments reflect publicly available studio information and our own editorial judgment—not verified third-party reviews or independent audits.";

export const LAST_UPDATED = "2026-10-11";

export const FIRST_PUBLISHED = "2026-06-06";

export const REVIEW_NOTES =
  "October 2026 review: verified booking links. DOCLAY NYC's advertised Jul 1–Aug 31 renovation closure has ended; the public site still lists the same Koreatown studio. We keep DOCLAY at #10 because the summer shutdown and a more exclusive post-renovation membership model continue to weigh on community and value. Top-9 order unchanged.";

export const STUDIOS: PotteryStudio[] = [
  {
    id: "gotham-pottery",
    rank: 2,
    name: "Gotham Pottery",
    neighborhood: "Chelsea",
    borough: "Manhattan",
    website: "https://gothampottery.com",
    description:
      "A Manhattan studio built around approachable, small-group workshops—wheel throwing and hand-building with no experience needed, plus private events.",
    highlights: [
      "No-experience-needed workshops",
      "Small-group, instructor-led sessions",
      "Date nights and private events",
      "Memberships are available, in a growing community",
    ],
    classTypes: ["Wheel throwing", "Hand-building", "Date nights", "Private events", "Studio membership"],
    ratings: {
      instruction: "exceptional",
      facilities: "excellent",
      community: "strong",
      value: "excellent",
      beginner: "exceptional",
    },
    accolade: "Best for Learning",
    editorialNote:
      "Our top choice if your goal is to actually learn pottery: it leads the field on instruction quality and beginner experience, with expert small-group teaching, no prerequisites, and classes you can book this month. It's also the studio we'd book first for a private event or group outing—easy to reserve without the membership waitlists elsewhere.",
  },
  {
    id: "greenwich-house-pottery",
    rank: 3,
    name: "Greenwich House Pottery",
    neighborhood: "West Village",
    borough: "Manhattan",
    website: "https://greenwichhouse.org/pottery/",
    description:
      "A nonprofit ceramics institution teaching since 1904, with historic West Village studios on Jones Street and a newer Chelsea space, plus an exceptional glaze and materials library.",
    highlights: [
      "Teaching ceramics since 1904",
      "Deep glaze and materials library",
      "Wheel, hand-building, and mold-making",
      "Two Manhattan locations",
    ],
    classTypes: ["Wheel throwing", "Hand-building", "Mold-making", "Open studio"],
    ratings: {
      instruction: "exceptional",
      facilities: "exceptional",
      community: "excellent",
      value: "strong",
      beginner: "solid",
    },
    accolade: "Most Historic Studio",
    editorialNote:
      "A storied nonprofit with deep facilities and a long teaching history—though spots are competitive and the atmosphere is serious rather than casual.",
  },
  {
    id: "bklyn-clay",
    rank: 1,
    name: "BKLYN CLAY",
    neighborhood: "Prospect Heights",
    borough: "Brooklyn",
    website: "https://bklynclay.com",
    description:
      "A state-of-the-art Brooklyn studio with advanced equipment, 12-week semester courses, and coveted 24/7 memberships, plus a Tribeca location in Manhattan.",
    highlights: [
      "State-of-the-art equipment",
      "12-week semester courses",
      "24/7 membership (long waitlist)",
      "Brooklyn and Tribeca locations",
    ],
    classTypes: ["Wheel throwing", "Hand-building", "Studio membership", "Workshops"],
    ratings: {
      instruction: "excellent",
      facilities: "exceptional",
      community: "excellent",
      value: "strong",
      beginner: "strong",
    },
    accolade: "Best Overall",
    editorialNote:
      "Our overall #1: 24/7 member access to the most polished, tech-forward equipment in the city, paired with one of the strongest maker communities around—it edges out the field on facilities. The catch is the long membership waitlist, so book a class or workshop to get a foot in the door while you wait.",
  },
  {
    id: "ninety-second-street-y",
    rank: 4,
    name: "92NY Ceramics Center",
    neighborhood: "Upper East Side",
    borough: "Manhattan",
    website: "https://www.92ny.org/culture-arts/school-of-the-arts/art-center",
    description:
      "The Upper East Side institution's newly rebuilt Ceramics Center, with 25 electric wheels, gas and electric kilns, small classes, and generous instructor time.",
    highlights: [
      "Brand-new 2026 studios",
      "25 electric wheels and multiple kilns",
      "Small classes, lots of instructor time",
      "Open studio for enrolled students",
    ],
    classTypes: ["Wheel throwing", "Hand-building", "Multi-week courses", "Open studio"],
    ratings: {
      instruction: "excellent",
      facilities: "exceptional",
      community: "excellent",
      value: "strong",
      beginner: "strong",
    },
    editorialNote:
      "A well-resourced institution with small classes and strong instruction—the rebuilt 2026 studios push facilities into exceptional territory.",
  },
  {
    id: "mud-matters",
    rank: 5,
    name: "Mud Matters",
    neighborhood: "Hell's Kitchen",
    borough: "Manhattan",
    website: "https://www.mudmatters.com",
    description:
      "A women-owned Hell's Kitchen studio (formerly Mud, Sweat & Tears) with an intimate, relaxed space, eight-week courses for all levels, and independent practice sessions.",
    highlights: [
      "Women-owned, 40+ year studio history",
      "Intimate, relaxed space",
      "Eight-week all-levels courses",
      "Independent practice sessions",
    ],
    classTypes: ["Wheel throwing", "Hand-building", "Open studio", "Workshops"],
    ratings: {
      instruction: "excellent",
      facilities: "strong",
      community: "excellent",
      value: "strong",
      beginner: "excellent",
    },
    editorialNote:
      "A warm, personal studio with strong value and community—great for steady weekly practice in a calm setting.",
  },
  {
    id: "clayworks-on-columbia",
    rank: 6,
    name: "Clayworks on Columbia",
    neighborhood: "Red Hook",
    borough: "Brooklyn",
    website: "https://www.clayworksoncolumbia.org",
    description:
      "A nonprofit Red Hook studio and pottery school since 1994, with 10-week classes, weekly open studio time, youth programs, and a storefront gallery.",
    highlights: [
      "Nonprofit since 1994",
      "10-week classes with open studio",
      "Youth and teen programs",
      "Storefront gallery",
    ],
    classTypes: ["Wheel throwing", "Hand-building", "Sculpture", "Open studio"],
    ratings: {
      instruction: "strong",
      facilities: "strong",
      community: "excellent",
      value: "excellent",
      beginner: "excellent",
    },
    editorialNote:
      "Excellent value and a welcoming community in a true neighborhood studio—intro paths are clearer than our last review suggested.",
  },
  {
    id: "choplet",
    rank: 7,
    name: "Choplet Ceramic Studio",
    neighborhood: "Williamsburg",
    borough: "Brooklyn",
    website: "https://choplet.com",
    description:
      "A long-running Williamsburg studio (est. 2005) with two indoor classrooms and a garden studio, offering wheel, hand-building, sculpture, kids' camps, and memberships across multiple Brooklyn locations.",
    highlights: [
      "Established Williamsburg studio (2005)",
      "Wheel, hand-building, and sculpture",
      "Kids and teen summer camps",
      "Multiple Brooklyn locations",
    ],
    classTypes: ["Wheel throwing", "Hand-building", "Kids classes", "Camps"],
    ratings: {
      instruction: "excellent",
      facilities: "excellent",
      community: "excellent",
      value: "strong",
      beginner: "strong",
    },
    editorialNote:
      "Versatile facilities and family-friendly programming, with one-day workshops and eight-week sessions for every level.",
  },
  {
    id: "brickhouse",
    rank: 8,
    name: "Brickhouse Ceramic Art Center",
    neighborhood: "Long Island City",
    borough: "Queens",
    website: "https://www.brickhouseceramic.com",
    description:
      "A Queens studio offering classes, studio space, and community events with a focus on ceramic art and sustained practice.",
    highlights: [
      "Queens waterfront-adjacent location",
      "Class and studio memberships",
      "Community events",
      "Room for longer-term practice",
    ],
    classTypes: ["Wheel throwing", "Hand-building", "Studio membership"],
    ratings: {
      instruction: "strong",
      facilities: "strong",
      community: "excellent",
      value: "strong",
      beginner: "solid",
    },
    editorialNote:
      "A standout for students who want room to grow beyond a single intro class, with one of the warmest communities in our roundup.",
  },
  {
    id: "la-mano",
    rank: 9,
    name: "La Mano Pottery",
    neighborhood: "Chelsea",
    borough: "Manhattan",
    website: "https://www.lamanopottery.com",
    description:
      "A Chelsea institution with wheel classes, studio time, and a focus on helping students build consistent throwing practice.",
    highlights: [
      "Chelsea location",
      "Wheel-focused instruction",
      "Studio time options",
      "Long-running NYC presence",
    ],
    classTypes: ["Wheel throwing", "Studio time", "Workshops"],
    ratings: {
      instruction: "strong",
      facilities: "strong",
      community: "solid",
      value: "strong",
      beginner: "strong",
    },
    editorialNote:
      "A solid, focused Manhattan option with a clear path from classes to independent practice—community and facilities are more modest than the larger studios.",
  },
  {
    id: "doclay-nyc",
    rank: 10,
    name: "DOCLAY NYC",
    neighborhood: "Koreatown",
    borough: "Manhattan",
    website: "https://www.doclaynyc.com",
    description:
      "A compact 7th-floor Midtown studio on West 32nd Street with multi-week wheel and hand-building semesters, Friday night intro sessions, Saturday paint-your-own workshops, and membership tiers. The studio closed entirely for renovations from Jul 1–Aug 31, 2026.",
    highlights: [
      "Compact Koreatown studio (7th floor)",
      "Multi-week wheel and hand-building semesters",
      "Do Clay Night intro sessions (Fridays)",
      "Full summer 2026 renovation closure (Jul 1–Aug 31)",
    ],
    classTypes: ["Wheel throwing", "Hand-building", "Workshops", "Studio membership"],
    ratings: {
      instruction: "strong",
      facilities: "solid",
      community: "solid",
      value: "solid",
      beginner: "strong",
    },
    editorialNote:
      "Members were upset that the studio closed entirely for summer renovations (Jul 1–Aug 31), leaving people mid-semester without wheel time. The advertised closure window has ended and the public site lists the same 7th-floor Koreatown address, which still runs tight at peak hours.",
  },
  {
    id: "togei-kyoshitsu",
    rank: 11,
    name: "Togei Kyoshitsu",
    neighborhood: "NoMad",
    borough: "Manhattan",
    website: "https://www.nytogei.com",
    description:
      "A quiet NoMad loft (est. 1994) teaching traditional Japanese ceramics—including throwing off the hump on a clockwise wheel—in English and Japanese, with member open-studio access.",
    highlights: [
      "Traditional Japanese techniques",
      "Clockwise-wheel throwing off the hump",
      "Classes in English and Japanese",
      "Calm, loft-like space",
    ],
    classTypes: ["Wheel throwing", "Hand-building", "Open studio", "Private lessons"],
    ratings: {
      instruction: "excellent",
      facilities: "strong",
      community: "strong",
      value: "solid",
      beginner: "solid",
    },
    editorialNote:
      "Falls to #11 only because the list expanded—still a niche standout for Japanese technique, and unlike DOCLAY, it stayed open through summer 2026. Less of a fit if you want a standard Western wheel curriculum.",
  },
];

export function getRankedStudios(): PotteryStudio[] {
  return [...STUDIOS].sort((a, b) => a.rank - b.rank);
}

export function getRankingsPayload() {
  const studios = getRankedStudios();
  return {
    disclaimer: EDITORIAL_DISCLAIMER,
    lastUpdated: LAST_UPDATED,
    firstPublished: FIRST_PUBLISHED,
    generatedAt: `${LAST_UPDATED}T12:00:00.000Z`,
    publishedAt: `${FIRST_PUBLISHED}T12:00:00.000Z`,
    reviewNotes: REVIEW_NOTES,
    schemaVersion: 1,
    criteria: RANKING_CRITERIA,
    studios,
  };
}
